<template>
  <div class="navigation-title-section">
    <div class="navigation-title-section-content">Artworks catalogue</div>
  </div>
  <div class="catalogue-page" v-loading="loading">
    <section class="panel">
      <div class="panel-header">
        <h3>Artworks</h3>
        <div class="panel-actions">
          <el-popover placement="bottom-end" trigger="click" :width="200">
            <template #reference>
              <el-button>
                Columns ({{ selectedColumns.length }})
                <i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
            </template>
            <el-checkbox-group v-model="selectedColumns" class="column-list">
              <el-checkbox
                v-for="column in columns"
                :key="column.key"
                :label="column.key"
              >
                {{ column.label }}
              </el-checkbox>
            </el-checkbox-group>
          </el-popover>
          <el-button type="primary" :disabled="!canExport" @click="exportDialog = true">
            Export / Share
          </el-button>
        </div>
      </div>
      <div class="filter-row">
        <el-input
          v-model="search"
          class="search-input"
          placeholder="Search by title or artist"
          clearable
        />
        <el-select
          v-model="artistFilter"
          class="artist-select"
          placeholder="All artists"
          filterable
          clearable
        >
          <el-option
            v-for="artist in artists"
            :key="artist.id"
            :label="artist.name"
            :value="artist.id"
          />
        </el-select>
        <el-checkbox v-model="showUnpublished">Show unpublished</el-checkbox>
      </div>
      <div class="selection-row">
        <span>
          <strong>{{ selectedArtworks.length }}</strong> selected ·
          {{ filteredArtworks.length }} shown
        </span>
        <el-button size="small" @click="selectShown(true)">Select shown</el-button>
        <el-button size="small" @click="selectShown(false)">Deselect shown</el-button>
        <el-button size="small" @click="selected = new Set()">Clear all</el-button>
      </div>

      <p v-if="!loading && filteredArtworks.length === 0" class="empty-notice">
        No artworks match these filters.
      </p>
      <div class="artwork-grid">
        <label
          v-for="artwork in filteredArtworks"
          :key="artwork.id"
          class="artwork-tile"
          :class="{ 'is-selected': selected.has(artwork.id) }"
        >
          <input
            type="checkbox"
            :checked="selected.has(artwork.id)"
            @change="toggle(artwork.id)"
          />
          <div class="artwork-thumb">
            <img
              v-if="artwork.image"
              :src="imageUrl(artwork.image)"
              :style="thumbStyle(artwork.image_ratio)"
              loading="lazy"
            />
          </div>
          <span class="artwork-title">{{ artwork.title }}</span>
          <span class="artwork-artist">{{ artwork.artist }}</span>
          <span v-if="!artwork.published" class="badge">Unpublished</span>
        </label>
      </div>
    </section>

    <el-dialog v-model="exportDialog" title="Export / Share" width="520px" @closed="newLink = ''">
      <p class="dialog-summary">
        {{ selectedArtworks.length }} artworks ·
        {{ chosenColumns.map((c) => c.label).join(", ") }}
      </p>
      <label class="dialog-label">Catalogue title</label>
      <el-input v-model="title" placeholder="Catalogue title" />

      <h4 class="dialog-heading">Download</h4>
      <div class="action-row">
        <el-button type="primary" :loading="pdfBusy" @click="savePdf">
          {{ pdfBusy ? pdfProgress : "Download PDF" }}
        </el-button>
        <el-button @click="downloadCsv">Download CSV</el-button>
      </div>
      <p class="hint">Large catalogues take a moment while the images download.</p>

      <h4 class="dialog-heading">Share link</h4>
      <div class="action-row">
        <el-select v-model="expiresInDays" class="expiry-select">
          <el-option
            v-for="option in expiryOptions"
            :key="option.label"
            :label="option.label"
            :value="option.value"
          />
        </el-select>
        <el-button type="primary" plain :loading="creating" @click="createLink">
          Create link
        </el-button>
      </div>
      <div v-if="newLink" class="new-link">
        <el-input :model-value="newLink" readonly />
        <el-button @click="copy(newLink)">Copy</el-button>
        <a :href="newLink" target="_blank" rel="noopener">Open</a>
      </div>
    </el-dialog>

    <section class="panel">
      <h3>Shared links</h3>
      <p v-if="catalogues.length === 0" class="empty-notice">No links created yet.</p>
      <el-table v-else :data="catalogues" size="small">
        <el-table-column label="Title" min-width="180">
          <template #default="{ row }">{{ row.title || "Untitled" }}</template>
        </el-table-column>
        <el-table-column label="Artworks" prop="artworks_count" width="90" />
        <el-table-column label="Created" width="120">
          <template #default="{ row }">{{ formatDate(row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="Expires" width="120">
          <template #default="{ row }">
            <span :class="{ expired: row.expired }">
              {{ row.expires_at ? formatDate(row.expires_at) : "Never" }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="" min-width="200">
          <template #default="{ row }">
            <el-button size="mini" @click="copy(linkFor(row))">Copy link</el-button>
            <a :href="linkFor(row)" target="_blank" rel="noopener" class="table-link">Open</a>
            <el-button size="mini" type="danger" plain @click="removeLink(row)">Delete</el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>
  </div>
</template>

<script>
import { ElNotification, ElMessageBox } from "element-plus";
import {
  catalogueUrl,
  exportCatalogueCsv,
  imageUrl,
  downloadCataloguePdf,
} from "@/helpers/artwork-catalogue";

const API = process.env.VUE_APP_SHARED_API;

export default {
  name: "ArtworkCatalogue",
  data() {
    return {
      loading: true,
      creating: false,
      exportDialog: false,
      pdfBusy: false,
      pdfProgress: "",
      columns: [],
      artworks: [],
      catalogues: [],
      selectedColumns: [],
      selected: new Set(),
      search: "",
      artistFilter: null,
      showUnpublished: false,
      title: "CERES Catalogue",
      expiresInDays: 30,
      expiryOptions: [
        { label: "Expires in 7 days", value: 7 },
        { label: "Expires in 30 days", value: 30 },
        { label: "Expires in 90 days", value: 90 },
        { label: "Never expires", value: null },
      ],
      newLink: "",
    };
  },
  computed: {
    artists() {
      const map = new Map();
      this.artworks.forEach((a) => {
        if (a.artist_id && a.artist) map.set(a.artist_id, a.artist);
      });
      return [...map]
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    filteredArtworks() {
      const term = this.search.trim().toLowerCase();
      return this.artworks.filter(
        (a) =>
          (this.showUnpublished || a.published) &&
          (!this.artistFilter || a.artist_id === this.artistFilter) &&
          (!term ||
            (a.title || "").toLowerCase().includes(term) ||
            (a.artist || "").toLowerCase().includes(term))
      );
    },
    selectedArtworks() {
      return this.artworks.filter((a) => this.selected.has(a.id));
    },
    chosenColumns() {
      return this.columns.filter((c) => this.selectedColumns.includes(c.key));
    },
    canExport() {
      return this.chosenColumns.length > 0 && this.selectedArtworks.length > 0;
    },
  },
  created() {
    this.loadArtworks();
    this.loadCatalogues();
  },
  methods: {
    imageUrl,
    // Stored medium images can be stretched, so draw them at the original's ratio.
    thumbStyle(ratio) {
      if (!ratio) return {};
      return ratio > 1.25
        ? { width: "100%", height: "auto", aspectRatio: ratio, objectFit: "fill" }
        : { height: "100%", width: "auto", aspectRatio: ratio, objectFit: "fill" };
    },
    linkFor(row) {
      return catalogueUrl(row.token);
    },
    notifyError(message) {
      ElNotification({ title: "Error!", message, type: "error" });
    },
    async loadArtworks() {
      try {
        const res = await this.$api.get(`${API}api/artwork-catalogues/artworks`);
        this.columns = res.data.columns;
        this.selectedColumns = this.columns.filter((c) => c.default).map((c) => c.key);
        this.artworks = res.data.artworks;
        this.selected = new Set(this.artworks.filter((a) => a.published).map((a) => a.id));
      } catch (_) {
        this.notifyError("Could not load artworks.");
      } finally {
        this.loading = false;
      }
    },
    async loadCatalogues() {
      try {
        const res = await this.$api.get(`${API}api/artwork-catalogues`);
        this.catalogues = res.data;
      } catch (_) {
        this.notifyError("Could not load shared links.");
      }
    },
    toggle(id) {
      const next = new Set(this.selected);
      next.has(id) ? next.delete(id) : next.add(id);
      this.selected = next;
    },
    selectShown(value) {
      const next = new Set(this.selected);
      this.filteredArtworks.forEach((a) => (value ? next.add(a.id) : next.delete(a.id)));
      this.selected = next;
    },
    fileName() {
      return (this.title || "catalogue").trim().replace(/[^\w-]+/g, "-").toLowerCase();
    },
    async savePdf() {
      this.pdfBusy = true;
      this.pdfProgress = "Preparing…";
      try {
        await downloadCataloguePdf({
          artworks: this.selectedArtworks,
          columns: this.chosenColumns,
          title: this.title || "Catalogue",
          fileName: this.fileName(),
          fetchImage: (artwork) =>
            this.$api
              .get(`${API}api/artwork-catalogues/artworks/${artwork.id}/image`, { responseType: "blob" })
              .then((res) => res.data),
          onProgress: (done, total) => {
            this.pdfProgress = done < total ? `Loading images ${done} / ${total}` : "Building PDF…";
          },
        });
      } catch (error) {
        this.notifyError("Could not create the PDF.");
      } finally {
        this.pdfBusy = false;
      }
    },
    downloadCsv() {
      try {
        exportCatalogueCsv(this.selectedArtworks, this.chosenColumns, this.fileName());
      } catch (error) {
        this.notifyError(error.message);
      }
    },
    async createLink() {
      this.creating = true;
      try {
        const res = await this.$api.post(`${API}api/artwork-catalogues`, {
          title: this.title,
          columns: this.chosenColumns.map((c) => c.key),
          artwork_ids: this.selectedArtworks.map((a) => a.id),
          expires_in_days: this.expiresInDays,
        });
        this.newLink = catalogueUrl(res.data.token);
        this.copy(this.newLink);
        this.loadCatalogues();
      } catch (_) {
        this.notifyError("Could not create the link.");
      } finally {
        this.creating = false;
      }
    },
    async removeLink(row) {
      try {
        await ElMessageBox.confirm(
          "People with this link will no longer be able to open the catalogue.",
          "Delete link?",
          { confirmButtonText: "Delete", cancelButtonText: "Cancel", type: "warning" }
        );
      } catch (_) {
        return;
      }
      try {
        await this.$api.delete(`${API}api/artwork-catalogues/${row.id}`);
        this.catalogues = this.catalogues.filter((c) => c.id !== row.id);
      } catch (_) {
        this.notifyError("Could not delete the link.");
      }
    },
    async copy(text) {
      try {
        await navigator.clipboard.writeText(text);
        ElNotification({ title: "Copied", message: "Link copied to clipboard.", type: "success" });
      } catch (_) {
        // Clipboard can be unavailable on plain http; the link is still shown to copy by hand.
      }
    },
    formatDate(value) {
      return value ? this.$moment(value).format("MMM D, YYYY") : "";
    },
  },
};
</script>

<style scoped>
.catalogue-page {
  padding: 0 16px 16px;
}
.panel {
  border: 1px solid #e4e4e4;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
}
.panel h3 {
  font-size: 16px;
  margin: 0 0 12px;
}
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
}
.panel-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}
.column-list .el-checkbox {
  margin: 0 0 6px;
}
.dialog-summary {
  color: #555;
  font-size: 13px;
  margin: 0 0 16px;
}
.dialog-label {
  display: block;
  font-size: 13px;
  margin-bottom: 6px;
}
.dialog-heading {
  font-size: 14px;
  margin: 20px 0 10px;
}
.filter-row,
.selection-row,
.action-row,
.new-link {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.search-input {
  max-width: 320px;
}
.artist-select {
  width: 240px;
}
.expiry-select {
  width: 200px;
}
.new-link .el-input {
  max-width: 520px;
}
.hint,
.empty-notice {
  color: #888;
  font-size: 13px;
}
.artwork-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  max-height: 448px;
  overflow-y: auto;
  padding: 2px;
}
.artwork-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid #e4e4e4;
  border-radius: 4px;
  padding: 6px;
  cursor: pointer;
  font-size: 12px;
  opacity: 0.55;
}
.artwork-tile.is-selected {
  border-color: #409eff;
  opacity: 1;
}
.artwork-tile input {
  position: absolute;
  top: 10px;
  left: 10px;
}
.artwork-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 110px;
  background: #f5f5f5;
  margin-bottom: 6px;
  overflow: hidden;
}
.artwork-thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.artwork-title {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.artwork-artist {
  color: #777;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #e6a23c;
  color: #fff;
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 3px;
}
.expired {
  color: #f56c6c;
}
.table-link {
  margin: 0 10px;
}
</style>
